import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CalendarClock, ChevronDown, Clock, RotateCcw, Terminal, X } from 'lucide-react';
import { cn } from '@/utils/cn';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CronSchedulerProps {
  value: string;
  onChange: (cron: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type PresetKey =
  | 'daily' | 'weekdays' | 'weekends'
  | 'hourly' | 'every_6h' | 'every_12h'
  | 'weekly_mon' | 'weekly_wed' | 'weekly_fri'
  | 'monthly' | 'custom';

interface Preset {
  key: PresetKey;
  label: string;
  icon: string;
  cron: string;
}

const PRESETS: Preset[] = [
  { key: 'daily', label: 'Daily', icon: '📅', cron: '0 {H} * * *' },
  { key: 'weekdays', label: 'Every Weekday', icon: '💼', cron: '0 {H} * * 1-5' },
  { key: 'weekends', label: 'Every Weekend', icon: '🏖️', cron: '0 {H} * * 0,6' },
  { key: 'weekly_mon', label: 'Every Monday', icon: '🟦', cron: '0 {H} * * 1' },
  { key: 'weekly_wed', label: 'Every Wednesday', icon: '🟩', cron: '0 {H} * * 3' },
  { key: 'weekly_fri', label: 'Every Friday', icon: '🟨', cron: '0 {H} * * 5' },
  { key: 'hourly', label: 'Every Hour', icon: '⏰', cron: '0 * * * *' },
  { key: 'every_6h', label: 'Every 6 Hours', icon: '🔄', cron: '0 */6 * * *' },
  { key: 'every_12h', label: 'Every 12 Hours', icon: '🌓', cron: '0 */12 * * *' },
  { key: 'monthly', label: 'Monthly (1st)', icon: '📆', cron: '0 {H} 1 * *' },
  { key: 'custom', label: 'Custom cron', icon: '✏️', cron: '' },
];

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i);
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const applyTime = (template: string, hour: number, minute: number) =>
  template.replace('{H}', String(hour)).replace('{M}', String(minute));

const isValidCron = (value: string): boolean => {
  const parts = value.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  return parts.every((p) =>
    /^(\*|(\d+(-\d+)?(,\d+(-\d+)?)*)(\/\d+)?|\*\/\d+)$/.test(p),
  );
};

function cronToHuman(cron: string): string {
  if (!cron || !isValidCron(cron)) return 'Not set';
  const [minute, hour, dom, , dow] = cron.trim().split(/\s+/);

  const timeStr = (h: string, m: string) => {
    if (h === '*' || h.startsWith('*/')) return '';
    const hNum = parseInt(h, 10);
    const mNum = parseInt(m, 10);
    const period = hNum < 12 ? 'AM' : 'PM';
    const h12 = hNum % 12 === 0 ? 12 : hNum % 12;
    return ` at ${h12}:${String(mNum).padStart(2, '0')} ${period}`;
  };

  if (hour === '*') return `Every hour at minute ${minute}`;
  if (hour.startsWith('*/')) return `Every ${hour.slice(2)} hours`;

  const t = timeStr(hour, minute);
  if (dom !== '*') return `Monthly on day ${dom}${t}`;
  if (dow === '*') return `Daily${t}`;
  if (dow === '1-5') return `Every weekday (Mon–Fri)${t}`;
  if (dow === '0,6' || dow === '6,0') return `Every weekend (Sat & Sun)${t}`;

  const dayNums = dow.split(',').map(Number);
  if (dayNums.length === 1) return `Every ${DAYS_FULL[dayNums[0]]}${t}`;
  return `Every ${dayNums.map((d) => DAYS_SHORT[d]).join(', ')}${t}`;
}

function detectPreset(cron: string): PresetKey {
  if (!cron || !isValidCron(cron)) return 'custom';
  const parts = cron.trim().split(/\s+/);
  const pattern = `0 {H} ${parts[2]} ${parts[3]} ${parts[4]}`;
  const found = PRESETS.find((p) => p.key !== 'custom' && p.cron === pattern);
  if (found) return found.key;
  if (parts[1] === '*') return 'hourly';
  if (parts[1] === '*/6') return 'every_6h';
  if (parts[1] === '*/12') return 'every_12h';
  return 'custom';
}

function extractTime(cron: string) {
  if (!cron) return { hour: 9, minute: 0 };
  const parts = cron.trim().split(/\s+/);
  const m = parseInt(parts[0], 10);
  const h = parseInt(parts[1], 10);
  return { hour: isNaN(h) ? 9 : h, minute: isNaN(m) ? 0 : m };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const CronScheduler: React.FC<CronSchedulerProps> = ({
  value,
  onChange,
  label = 'Recurring Schedule',
  placeholder = 'Set schedule…',
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<PresetKey>(() => detectPreset(value));
  const [hour, setHour] = useState(() => extractTime(value).hour);
  const [minute, setMinute] = useState(() => extractTime(value).minute);
  const [customCron, setCustomCron] = useState(value || '');
  const [customError, setCustomError] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync when value changes from outside
  useEffect(() => {
    if (!value) return;
    const detected = detectPreset(value);
    const { hour: h, minute: m } = extractTime(value);
    setActivePreset(detected);
    setHour(h);
    setMinute(m);
    if (detected === 'custom') setCustomCron(value);
  }, [value]);

  const buildCron = useCallback(
    (preset: PresetKey, h: number, m: number): string => {
      if (preset === 'custom') return customCron;
      const p = PRESETS.find((x) => x.key === preset)!;
      return applyTime(p.cron, h, m);
    },
    [customCron],
  );

  const handlePresetClick = (preset: PresetKey) => {
    setActivePreset(preset);
    if (preset !== 'custom') {
      const newCron = buildCron(preset, hour, minute);
      onChange(newCron);
    }
  };

  const handleTimeChange = (newHour: number, newMinute: number) => {
    setHour(newHour);
    setMinute(newMinute);
    if (activePreset !== 'custom') {
      onChange(buildCron(activePreset, newHour, newMinute));
    }
  };

  const handleCustomChange = (raw: string) => {
    setCustomCron(raw);
    if (isValidCron(raw)) {
      setCustomError('');
      onChange(raw);
    } else {
      setCustomError('Format: minute hour day month weekday');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setCustomCron('');
    setActivePreset('daily');
    setIsOpen(false);
  };

  const currentCron = value || buildCron(activePreset, hour, minute);
  const human = cronToHuman(currentCron);
  const isHourly = ['hourly', 'every_6h', 'every_12h'].includes(activePreset);
  const hasValue = !!value && isValidCron(value);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Label */}
      {label && (
        <label className="text-xs font-medium mb-1 block text-gray-700">
          {label}
        </label>
      )}

      {/* ── Trigger ───────────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 h-10 px-3 border rounded-lg text-sm transition-all duration-150',
          'bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400',
          isOpen
            ? 'border-indigo-500 ring-2 ring-indigo-300'
            : 'border-gray-300',
          !hasValue && 'text-gray-400',
        )}
      >
        <CalendarClock size={15} className={hasValue ? 'text-indigo-500' : 'text-gray-400'} />

        <span className={cn('flex-1 text-left truncate', hasValue ? 'text-gray-800' : 'text-gray-400')}>
          {hasValue ? human : placeholder}
        </span>

        {hasValue && (
          <code className="shrink-0 text-[11px] font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
            {currentCron}
          </code>
        )}

        {hasValue ? (
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 text-gray-400 hover:text-red-500 transition-colors ml-1"
            title="Clear schedule"
          >
            <X size={13} />
          </button>
        ) : (
          <ChevronDown
            size={14}
            className={cn(
              'shrink-0 text-gray-400 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        )}
      </button>

      {/* ── Dropdown panel ────────────────────────────────────────────────── */}
      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full min-w-[340px] bg-white border border-gray-200 rounded-xl shadow-xl p-4 space-y-4 left-0">
          {/* Preset chips */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
              Quick Presets
            </p>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handlePresetClick(p.key)}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150',
                    activePreset === p.key
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700',
                  )}
                >
                  <span className="text-sm leading-none">{p.icon}</span>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Time picker — hidden for hourly/custom */}
          {!isHourly && activePreset !== 'custom' && (
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
              <Clock size={13} className="text-gray-400 shrink-0" />
              <span className="text-xs text-gray-500 font-medium shrink-0">Run at</span>

              <select
                value={hour}
                onChange={(e) => handleTimeChange(Number(e.target.value), minute)}
                className="h-10 border border-gray-300 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                {HOUR_OPTIONS.map((h) => {
                  const period = h < 12 ? 'AM' : 'PM';
                  const h12 = h % 12 === 0 ? 12 : h % 12;
                  return (
                    <option key={h} value={h}>
                      {String(h12).padStart(2, '0')} {period}
                    </option>
                  );
                })}
              </select>

              <span className="text-gray-400 font-bold text-sm">:</span>

              <select
                value={minute}
                onChange={(e) => handleTimeChange(hour, Number(e.target.value))}
                className="h-10 border border-gray-300 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                {MINUTE_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom cron input */}
          {activePreset === 'custom' && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 bg-gray-900 rounded-lg px-3 py-2.5">
                <Terminal size={13} className="text-green-400 shrink-0" />
                <input
                  type="text"
                  value={customCron}
                  onChange={(e) => handleCustomChange(e.target.value)}
                  placeholder="e.g. 0 9 * * 1-5"
                  className="flex-1 bg-transparent text-green-300 text-sm font-mono placeholder-gray-600 focus:outline-none"
                  spellCheck={false}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => handleCustomChange('0 9 * * *')}
                  title="Reset"
                  className="text-gray-500 hover:text-green-400 transition-colors"
                >
                  <RotateCcw size={11} />
                </button>
              </div>
              {customError ? (
                <p className="text-red-500 text-[11px]">{customError}</p>
              ) : (
                <p className="text-gray-400 text-[11px] font-mono">
                  min&nbsp; hour&nbsp; day-of-month&nbsp; month&nbsp; day-of-week
                </p>
              )}
            </div>
          )}

          {/* Live preview + Apply */}
          <div className="flex items-center justify-between gap-3 pt-1 border-t border-gray-100">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Result</p>
              <p className="text-sm font-semibold text-gray-800 truncate">{human}</p>
              <code className="text-[11px] font-mono text-indigo-600">{currentCron}</code>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CronScheduler;